import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProfilingApi } from './api';
import type {
  CollectProfileRequest,
  CompareProfilesRequest,
  ListProfilesParams,
  ProfilingConfig,
} from './types';

const QUERY_KEYS = {
  all: ['profiling'] as const,
  profiles: () => [...QUERY_KEYS.all, 'profiles'] as const,
  profileList: (params?: ListProfilesParams) => [...QUERY_KEYS.profiles(), 'list', params] as const,
  profileDetail: (id: string) => [...QUERY_KEYS.profiles(), 'detail', id] as const,
  profileAnalysis: (id: string) => [...QUERY_KEYS.profiles(), 'analysis', id] as const,
  config: () => [...QUERY_KEYS.all, 'config'] as const,
  runtime: () => [...QUERY_KEYS.all, 'runtime'] as const,
};

/**
 * Hook to fetch profiles list with optional filtering
 */
export const useProfiles = (params?: ListProfilesParams) => {
  return useQuery({
    queryKey: QUERY_KEYS.profileList(params),
    queryFn: () => ProfilingApi.listProfiles(params),
  });
};

/**
 * Hook to fetch a single profile by ID
 */
export const useProfile = (profileId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.profileDetail(profileId),
    queryFn: () => ProfilingApi.getProfile(profileId),
    enabled: !!profileId,
  });
};

/**
 * Hook to fetch profile analysis
 */
export const useProfileAnalysis = (profileId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.profileAnalysis(profileId),
    queryFn: () => ProfilingApi.analyzeProfile(profileId),
    enabled: !!profileId,
  });
};

/**
 * Hook to collect a new profile
 */
export const useCollectProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CollectProfileRequest) => ProfilingApi.collectProfile(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.profiles() });
    },
  });
};

/**
 * Hook to delete a profile
 */
export const useDeleteProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profileId: string) => ProfilingApi.deleteProfile(profileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.profiles() });
    },
  });
};

/**
 * Hook to compare two profiles
 */
export const useCompareProfiles = () => {
  return useMutation({
    mutationFn: (request: CompareProfilesRequest) => ProfilingApi.compareProfiles(request),
  });
};

/**
 * Hook to fetch profiling configuration
 */
export const useProfilingConfig = () => {
  return useQuery({
    queryKey: QUERY_KEYS.config(),
    queryFn: () => ProfilingApi.getConfig(),
  });
};

/**
 * Hook to update profiling configuration
 */
export const useUpdateProfilingConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: Partial<ProfilingConfig>) => ProfilingApi.updateConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.config() });
    },
  });
};

/**
 * Hook to fetch current runtime statistics
 * Auto-refreshes every 30 seconds
 */
export const useRuntimeStats = (enabled = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.runtime(),
    queryFn: () => ProfilingApi.getRuntimeStats(),
    enabled,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

/**
 * Export query keys for external use
 */
export { QUERY_KEYS as profilingQueryKeys };
